/**
 * Shared audio utilities — single source of truth for MIME type selection.
 *
 * Used by:
 *  - chat RecordDock  (use-audio-recording.tsx)
 *  - chat CTA         (cta.hooks.ts)
 *  - onboarding       (use-audio-recording.ts)
 */

/**
 * Pick the best recording MIME type supported by this browser.
 *
 * Priority:
 *  1. WebM+Opus  — Chrome / Edge / Firefox (widest backend compat)
 *  2. MP4        — Safari / iOS
 *  3. OGG+Opus   — fallback
 *
 * Returns empty string if nothing is supported (caller should error).
 */
export const pickMimeType = (): string => {
  const candidates = [
    // Best for Chrome/Edge/Firefox
    'audio/webm;codecs=opus',
    'audio/webm',

    // Best for Safari/iOS (often works as m4a/mp4)
    'audio/mp4',
    'audio/mp4;codecs=mp4a.40.2',

    // Sometimes supported
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  const isSupported = (t: string) => {
    try {
      return (
        typeof MediaRecorder !== 'undefined' &&
        MediaRecorder.isTypeSupported(t)
      );
    } catch {
      return false;
    }
  };

  return candidates.find(isSupported) || '';
};
